<template>
  <div class="group-metrics-panel">
    <!-- ──────────────────────────────────────────────
         SERIES IDENTITY · only with a scope (analysisGroups on)
         ────────────────────────────────────────────── -->
    <div v-if="scope" class="ve-series-line">
      <v-chip
        v-for="chip in engineVersionChips"
        :key="chip.key"
        x-small
        outlined
        label
        class="ve-series-line__chip"
      >
        {{ chip.text }}
      </v-chip>
      <v-chip
        v-if="scope.config && scope.config.version"
        x-small
        outlined
        label
        class="ve-series-line__chip"
      >
        config v{{ scope.config.version }}
      </v-chip>
      <span class="caption grey--text ve-series-line__caption">{{ scopeCaption }}</span>
      <v-chip
        v-if="scope.mixed_series"
        x-small
        color="warning"
        dark
        label
        class="ve-series-line__chip ve-mixed-series-chip"
        title="The evaluated runs come from different engine versions or configurations; the metrics mix series."
      >
        <v-icon x-small left>mdi-alert-outline</v-icon>
        Mixed series
      </v-chip>
    </div>

    <!-- ──────────────────────────────────────────────
         CORPUS · incoherence, truthful, labeled and total videos
         ────────────────────────────────────────────── -->
    <v-card outlined class="ve-counts-card ve-counts-card--corpus">
      <div class="ve-count">
        <div class="ve-count__value">{{ global.videos_with_incoherence }}</div>
        <div class="ve-count__label">Lie videos</div>
      </div>
      <v-divider vertical />
      <div class="ve-count">
        <div class="ve-count__value">{{ global.videos_truthful }}</div>
        <div class="ve-count__label">Truthful videos</div>
      </div>
      <v-divider vertical />
      <div class="ve-count">
        <div class="ve-count__value">{{ global.videos_labeled }}</div>
        <div class="ve-count__label">Labeled videos</div>
      </div>
      <v-divider vertical />
      <div class="ve-count">
        <div class="ve-count__value">{{ global.videos_total }}</div>
        <div class="ve-count__label">
          {{ isGroupScope ? 'Videos in group' : 'Videos in system' }}
        </div>
      </div>
      <v-divider vertical />
      <div
        class="ve-count"
        title="Runs executed without the coherence engine; excluded from every metric"
      >
        <div class="ve-count__value">{{ global.videos_coherence_not_requested }}</div>
        <div class="ve-count__label">Coherence not requested</div>
      </div>
      <v-chip v-if="isOverrideActive" small color="primary" outlined class="ve-corpus-chip">
        threshold override {{ activeThresholdPct }}%
      </v-chip>
    </v-card>

    <!-- ──────────────────────────────────────────────
         HERO · the three per-video metrics, per score method
         ────────────────────────────────────────────── -->
    <div class="ve-hero">
      <v-card v-for="m in methodDefs" :key="m.key" outlined class="ve-method-card">
        <div class="ve-method-card__title">
          <v-icon small class="mr-1">{{ m.icon }}</v-icon>
          {{ m.label }}
        </div>
        <div class="ve-kpi-grid">
          <div
            v-for="k in kpiDefs"
            :key="k.key"
            class="ve-kpi"
            :class="kpiClass(kpiStatus(m.key, k))"
          >
            <div class="ve-kpi__value">{{ formatPct(methodGlobal(m.key)[k.pctField]) }}</div>
            <div class="ve-kpi__label">
              {{ k.label }}
              <span v-if="k.hint" class="ve-kpi__hint">({{ k.hint }})</span>
            </div>
            <div class="ve-kpi__subtitle">{{ kpiSubtitle(m.key, k) }}</div>
            <div class="ve-kpi__target">
              <v-icon x-small :color="statusColor(kpiStatus(m.key, k))">mdi-circle</v-icon>
              {{ kpiTarget(k) }}
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <!-- ──────────────────────────────────────────────
         BUCKETS · anti-survivorship lists (group scope only)
         ────────────────────────────────────────────── -->
    <template v-if="showBuckets">
      <v-alert v-if="pendingCount > 0" dense text type="info" class="ve-bucket-alert">
        {{ pendingCount }} {{ pendingCount === 1 ? 'video is' : 'videos are' }} still queued or
        running · metrics count them as not analysed.
        <router-link to="/analysis-queue" class="ml-1">Open analysis queue</router-link>
      </v-alert>
      <v-expansion-panels
        v-if="bucketDefs.some((b) => b.count > 0)"
        flat
        multiple
        class="ve-buckets"
      >
        <v-expansion-panel
          v-for="bucket in bucketDefs"
          :key="bucket.key"
          :disabled="bucket.count === 0"
          class="ve-bucket"
          :class="`ve-bucket--${bucket.key}`"
        >
          <v-expansion-panel-header class="ve-bucket__header">
            <span>
              <v-icon small :color="bucket.color" class="mr-2">{{ bucket.icon }}</v-icon>
              {{ bucket.label }}
              <v-chip x-small :color="bucket.color" dark class="ml-2 ve-bucket__count">
                {{ bucket.count }}
              </v-chip>
              <span class="caption grey--text ml-2">{{ bucket.hint }}</span>
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-list dense class="ve-bucket__list">
              <v-list-item
                v-for="item in bucket.items"
                :key="`${item.source_type}-${item.source_id}`"
                class="ve-bucket__item"
                @click="goToSource(item)"
              >
                <v-list-item-icon class="mr-2">
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ item.video_name || sourceLabel(item) }}</v-list-item-title>
                  <v-list-item-subtitle>{{ bucketItemSubtitle(item) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="item.label_type" class="my-0">
                  <v-chip
                    x-small
                    outlined
                    :color="item.label_type === 'truthful' ? 'success' : 'error'"
                  >
                    {{ item.label_type === 'truthful' ? 'Truthful' : 'Lie' }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <!-- ──────────────────────────────────────────────
         ADVANCED · collapsed by default
         ────────────────────────────────────────────── -->
    <v-expansion-panels v-model="advancedPanel" flat class="ve-advanced">
      <v-expansion-panel>
        <v-expansion-panel-header class="ve-advanced__header">
          <span>
            <v-icon small class="mr-2">mdi-tune-variant</v-icon>
            Advanced metrics
            <span class="caption grey--text ml-2">
              Corpus counts, threshold override and idea-level metrics
            </span>
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="ve-summary">
            <v-card outlined class="ve-counts-card">
              <div class="ve-count">
                <div class="ve-count__value">{{ global.videos_total }}</div>
                <div class="ve-count__label">Videos total</div>
              </div>
              <v-divider vertical />
              <div class="ve-count">
                <div class="ve-count__value">{{ global.videos_labeled }}</div>
                <div class="ve-count__label">Labeled</div>
              </div>
              <v-divider vertical />
              <div class="ve-count">
                <div class="ve-count__value">{{ global.videos_evaluated }}</div>
                <div class="ve-count__label">Evaluated</div>
              </div>
            </v-card>

            <v-card outlined class="ve-threshold-card">
              <div class="ve-threshold-card__label">
                Subtraction threshold (%)
                <v-chip v-if="isOverrideActive" x-small color="primary" outlined class="ml-1">
                  override
                </v-chip>
              </div>
              <div class="ve-threshold-card__hint">
                Experiment override applied to both methods — ideas scoring below this count as
                subtracting. In use: <strong>{{ activeThresholdPct }}%</strong>
              </div>
              <div class="ve-threshold-card__controls">
                <v-text-field
                  v-model="thresholdInput"
                  type="number"
                  min="0"
                  max="100"
                  dense
                  outlined
                  hide-details="auto"
                  :error-messages="thresholdError"
                  :placeholder="String(global.default_threshold_pct)"
                  class="ve-threshold-card__input"
                  @keyup.enter="applyThreshold"
                />
                <v-btn small color="primary" depressed @click="applyThreshold">Apply</v-btn>
                <v-btn v-if="isOverrideActive" small text @click="resetThreshold">Reset</v-btn>
              </div>
            </v-card>
          </div>

          <div class="ve-hero ve-hero--advanced">
            <v-card v-for="m in methodDefs" :key="m.key" outlined class="ve-method-card">
              <div class="ve-method-card__title">
                <v-icon small class="mr-1">{{ m.icon }}</v-icon>
                {{ m.label }}
              </div>
              <div class="ve-method-section">
                <div class="ve-method-section__title">
                  <v-icon x-small class="mr-1">mdi-video-outline</v-icon>
                  By video
                </div>
                <div class="ve-method-card__grid">
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).correctness_pct) }}
                    </div>
                    <div class="ve-method-stat__label">Correctness (labeled)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).correct_videos }} of {{ global.videos_labeled }}
                      labeled videos
                    </div>
                  </div>
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).correctness_pct_over_total) }}
                    </div>
                    <div class="ve-method-stat__label">Correctness (all videos)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).correct_videos }} of {{ global.videos_total }}
                      videos in the system
                    </div>
                  </div>
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).false_positive_video_pct) }}
                    </div>
                    <div class="ve-method-stat__label">False positive videos</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).videos_with_false_positive }} of
                      {{ global.videos_labeled }} labeled videos
                    </div>
                  </div>
                </div>
              </div>

              <v-divider class="ve-method-section__divider" />

              <div class="ve-method-section">
                <div class="ve-method-section__title">
                  <v-icon x-small class="mr-1">mdi-text-box-outline</v-icon>
                  By idea
                </div>
                <div class="ve-method-card__grid ve-method-card__grid--pairs">
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).correctness_pct_over_subtracting_ideas) }}
                    </div>
                    <div class="ve-method-stat__label">Correctness (subtracting ideas)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).correct_ideas_total }} of
                      {{ methodGlobal(m.key).subtracting_ideas_total }} subtracting ideas fall
                      inside a lie
                    </div>
                  </div>
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).correctness_pct_over_incoherence_ideas) }}
                    </div>
                    <div class="ve-method-stat__label">Correctness (all ideas)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).correct_ideas_full_coverage }} of
                      {{ methodGlobal(m.key).incoherence_ideas_total_full_coverage }} ideas inside lie
                      windows subtract ({{ methodGlobal(m.key).videos_full_coverage }} videos with
                      full idea coverage)
                    </div>
                  </div>
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).false_positive_pct) }}
                    </div>
                    <div class="ve-method-stat__label">False positives (subtracting ideas)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).false_positive_ideas_total }} of
                      {{ methodGlobal(m.key).subtracting_ideas_total }} subtracting ideas
                    </div>
                  </div>
                  <div class="ve-method-stat">
                    <div class="ve-method-stat__value">
                      {{ formatPct(methodGlobal(m.key).false_positive_pct_over_all_ideas) }}
                    </div>
                    <div class="ve-method-stat__label">False positives (all ideas)</div>
                    <div class="ve-method-stat__subtitle">
                      {{ methodGlobal(m.key).false_positive_ideas_full_coverage }} of
                      {{ methodGlobal(m.key).ideas_total_full_coverage }} ideas in
                      {{ methodGlobal(m.key).videos_full_coverage }} videos with full idea coverage
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- ──────────────────────────────────────────────
         BREAKDOWN · per-video table
         ────────────────────────────────────────────── -->
    <div class="ve-section">
      <div class="ve-section__title">
        <v-icon small class="mr-2">mdi-table-search</v-icon>
        Breakdown by video
        <span class="caption grey--text ml-2">How each labeled video was classified</span>
        <v-spacer />
        <v-btn-toggle v-model="selectedMethod" mandatory dense class="ve-method-toggle">
          <v-btn v-for="m in methodDefs" :key="m.key" :value="m.key" small text>
            {{ m.label }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <v-card v-if="!videoRows.length" outlined class="ve-card">
        <div class="ve-empty">
          <v-icon large color="grey lighten-1">mdi-movie-search-outline</v-icon>
          <div class="subtitle-2 grey--text mt-2">
            {{ isGroupScope ? 'No labeled videos in this group' : 'No labeled videos yet' }}
          </div>
          <div class="caption grey--text">
            Label a lie range or mark a video as truthful to start evaluating the analysis.
          </div>
        </div>
      </v-card>

      <v-card v-else outlined class="ve-card">
        <v-data-table
          :headers="videoHeaders"
          :items="videoRows"
          :items-per-page="10"
          dense
          class="ve-table"
        >
          <template #[`item.video_name`]="{ item }">
            <v-btn x-small text color="primary" @click="goToSource(item)">
              <v-icon x-small left>mdi-open-in-new</v-icon>
              {{ item.video_name || item.label }}
            </v-btn>
          </template>
          <template #[`item.label_type`]="{ item }">
            <v-chip x-small :color="item.label_type === 'truthful' ? 'success' : 'error'" outlined>
              {{ item.label_type === 'truthful' ? 'Truthful' : 'Lie' }}
            </v-chip>
          </template>
          <template #[`item.incoherence_range`]="{ item }">
            <span class="ve-mono">{{ item.incoherence_range }}</span>
          </template>
          <template #[`item.method_in_window`]="{ item }">
            {{ item.method_in_window === null ? '—' : item.method_in_window }}
          </template>
          <template #[`item.run`]="{ item }">
            <v-chip
              v-if="item.bucket"
              x-small
              outlined
              :color="bucketPresentation(item.bucket).color"
              class="ve-run-chip"
              :title="item.run_id || ''"
            >
              {{ bucketPresentation(item.bucket).label }}
            </v-chip>
            <span v-else-if="item.run_id" class="ve-mono">{{ shortRun(item.run_id) }}</span>
            <span v-else class="grey--text">—</span>
          </template>
          <template #[`item.result`]="{ item }">
            <!-- Run executed without the coherence engine: excluded from every metric -->
            <v-chip
              v-if="item.coherence_status === 'not_requested'"
              x-small
              outlined
              color="grey"
              class="ve-not-requested-chip"
            >
              Not requested
            </v-chip>
            <template v-else>
              <v-chip x-small :color="outcomeColor(item)" dark>{{ outcomeText(item) }}</v-chip>
              <span v-if="!item.analysis_available" class="caption grey--text ml-1">
                no analysis
              </span>
            </template>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import {
  bucketLists,
  bucketPresentation,
  formatEngineVersions,
  sourceRouteWithRun,
} from '@/utils/groupResolution';

const METHOD_DEFS = [
  { key: 'computed_subtraction', label: 'Alerts Subtraction', icon: 'mdi-minus-circle-outline' },
  { key: 'alert_major', label: 'Alert Major', icon: 'mdi-alert-circle-outline' },
];

/**
 * The three per-video tracking metrics, in display order. Each one reads a
 * count and a percentage from the method's global metrics and a denominator
 * from the corpus counts. Targets come from the certification plan
 * (correctness ≥ 70 % of videos, false positives ≤ 2 %).
 * - Lies detected (true positives): lie videos the analysis flagged as lie.
 * - False alarms (false positives): truthful videos the analysis flagged as lie.
 * - Correct videos: labeled videos the analysis got right (true positives + true negatives).
 */
const KPI_DEFS = [
  {
    key: 'true_positives',
    label: 'Lies detected',
    hint: 'true positives',
    countField: 'incoherents_detected',
    pctField: 'incoherents_detected_pct',
    denominatorField: 'videos_with_incoherence',
    subtitle: 'lie videos detected',
    target: 70,
    direction: 'min',
  },
  {
    key: 'false_positives',
    label: 'False alarms',
    hint: 'false positives',
    countField: 'truthful_flagged',
    pctField: 'truthful_flagged_pct',
    denominatorField: 'videos_truthful',
    subtitle: 'truthful videos flagged as lie',
    target: 2,
    direction: 'max',
  },
  {
    key: 'accuracy',
    label: 'Correct videos',
    hint: null,
    countField: 'correct_videos',
    pctField: 'correctness_pct',
    denominatorField: 'videos_labeled',
    subtitle: 'labeled videos classified correctly',
    target: 70,
    direction: 'min',
  },
];

const ADVANCED_PANEL_STORAGE_KEY = 'coherenceEvaluation.advancedOpen';

/** Per-video outcome → result chip in the breakdown table (same wording as the KPIs). */
const OUTCOME_DEFS = {
  incoherent_detected: { text: 'Detected', color: 'success' },
  incoherent_missed: { text: 'Missed', color: 'error' },
  false_positive: { text: 'False alarm', color: 'error' },
  truthful_cleared: { text: 'Cleared', color: 'success' },
};

export const EMPTY_METHOD_GLOBAL = Object.freeze({
  incoherents_detected: 0,
  incoherents_detected_pct: null,
  truthful_flagged: 0,
  truthful_flagged_pct: null,
  correct_videos: 0,
  correctness_pct: null,
  correctness_pct_over_total: null,
  videos_with_false_positive: 0,
  false_positive_video_pct: null,
  false_positive_pct: null,
  false_positive_pct_over_all_ideas: null,
  subtracting_ideas_total: 0,
  false_positive_ideas_total: 0,
  ideas_total_full_coverage: 0,
  false_positive_ideas_full_coverage: 0,
  videos_full_coverage: 0,
  correct_ideas_total: 0,
  correctness_pct_over_subtracting_ideas: null,
  incoherence_ideas_total_full_coverage: 0,
  correct_ideas_full_coverage: 0,
  correctness_pct_over_incoherence_ideas: null,
});

export const EMPTY_GLOBAL = Object.freeze({
  videos_total: 0,
  videos_labeled: 0,
  videos_with_incoherence: 0,
  videos_truthful: 0,
  videos_evaluated: 0,
  videos_coherence_not_requested: 0,
  default_threshold_pct: 50,
  methods: {
    computed_subtraction: { ...EMPTY_METHOD_GLOBAL },
    alert_major: { ...EMPTY_METHOD_GLOBAL },
  },
});

/**
 * Normalizes a `/video-incoherence/metrics` (or group metrics) payload into `{ global, byVideo, scope }`
 * with every field the panel reads present, tolerating an extra `{ data }` wrapper.
 */
export function normalizeMetricsPayload(data) {
  const payload = data && data.global ? data : (data && data.data) || {};
  const globalRaw = payload.global || {};
  const methodsRaw = globalRaw.methods || {};
  return {
    global: {
      ...EMPTY_GLOBAL,
      ...globalRaw,
      methods: {
        computed_subtraction: {
          ...EMPTY_METHOD_GLOBAL,
          ...(methodsRaw.computed_subtraction || {}),
        },
        alert_major: { ...EMPTY_METHOD_GLOBAL, ...(methodsRaw.alert_major || {}) },
      },
    },
    byVideo: Array.isArray(payload.byVideo) ? payload.byVideo : [],
    scope: payload.scope && typeof payload.scope === 'object' ? payload.scope : null,
  };
}

/**
 * The incoherence-evaluation metrics block (F15): corpus strip, the three PO metrics per score method,
 * advanced metrics (folded) and the per-video breakdown. Extracted from CoherenceEvaluation
 * without changing texts or numbers; with a `scope` (group metrics, analysisGroups on) it adds
 * the series identity line, the 'Mixed series' chip and the anti-survivorship bucket lists.
 *
 * The threshold override is owned by the parent (`appliedThreshold`); the panel emits
 * `apply-threshold` with a number or `null` (reset).
 */
export default {
  name: 'GroupMetricsPanel',

  props: {
    global: { type: Object, default: () => ({ ...EMPTY_GLOBAL }) },
    byVideo: { type: Array, default: () => [] },
    /** null = configured default threshold; a number = experiment override. */
    appliedThreshold: { type: Number, default: null },
    /** `scope` block of the metrics payload (group metrics). Null keeps the legacy rendering. */
    scope: { type: Object, default: null },
    /** Renders the Not analysed / Degraded / Not requested lists. */
    showBuckets: { type: Boolean, default: false },
    /** Adds the Run column (resolution bucket per video) to the breakdown table. */
    showRunColumn: { type: Boolean, default: false },
  },

  data() {
    return {
      methodDefs: METHOD_DEFS,
      kpiDefs: KPI_DEFS,
      selectedMethod: 'computed_subtraction',
      // v-expansion-panels model: 0 = open, undefined = collapsed (default).
      advancedPanel: localStorage.getItem(ADVANCED_PANEL_STORAGE_KEY) === '1' ? 0 : undefined,
      thresholdInput: '',
      thresholdError: '',
    };
  },

  computed: {
    isOverrideActive() {
      return this.appliedThreshold !== null;
    },

    activeThresholdPct() {
      return this.isOverrideActive ? this.appliedThreshold : this.global.default_threshold_pct;
    },

    isGroupScope() {
      return Boolean(this.scope && this.scope.kind === 'group');
    },

    engineVersionChips() {
      const versions = this.scope && this.scope.engine_versions;
      if (!versions || typeof versions !== 'object') return [];
      const text = formatEngineVersions(versions, { max: 7 });
      return text ? text.split(' · ').map((part) => ({ key: part.split(' ')[0], text: part })) : [];
    },

    scopeCaption() {
      if (!this.scope) return '';
      const parts = [];
      parts.push(this.isGroupScope ? `Scope: group #${this.scope.group_id}` : 'Scope: global');
      parts.push(`${this.global.videos_labeled} labelled videos`);
      if (this.scope.threshold_pct != null) parts.push(`threshold ${this.scope.threshold_pct}%`);
      return parts.join(' · ');
    },

    lists() {
      return bucketLists(this.byVideo);
    },

    pendingCount() {
      return this.byVideo.filter((row) => row && row.bucket === 'pending').length;
    },

    bucketDefs() {
      const count = (key) => {
        const fromList = this.lists[key].length;
        if (fromList) return fromList;
        const fromGlobal = Number(this.global[key]);
        return Number.isFinite(fromGlobal) ? fromGlobal : 0;
      };
      return [
        {
          key: 'not_analysed',
          label: 'Not analysed',
          hint: 'queued, running, failed, never run or pinned run missing · incoherents count as missed, truthful never as false alarms',
          color: 'warning',
          icon: 'mdi-alert-circle-outline',
          count: count('not_analysed'),
          items: this.lists.not_analysed,
        },
        {
          key: 'degraded',
          label: 'Degraded',
          hint: 'evaluated with a degraded run · counted normally',
          color: 'error',
          icon: 'mdi-alert-decagram-outline',
          count: count('degraded'),
          items: this.lists.degraded,
        },
        {
          key: 'not_requested',
          label: 'Not requested',
          hint: 'coherence engine not executed · excluded from every denominator',
          color: 'grey',
          icon: 'mdi-power-plug-off',
          count: count('not_requested'),
          items: this.lists.not_requested,
        },
      ];
    },

    videoHeaders() {
      const headers = [
        { text: 'Video', value: 'video_name', sortable: false },
        { text: 'Label', value: 'label_type', sortable: false, width: 100 },
        { text: 'Lie window', value: 'incoherence_range', sortable: false, width: 170 },
        { text: 'Flagged ideas', value: 'method_flagged', width: 120 },
        { text: 'In lie window', value: 'method_in_window', width: 120 },
      ];
      if (this.showRunColumn)
        headers.push({ text: 'Run', value: 'run', sortable: false, width: 130 });
      headers.push({ text: 'Result', value: 'result', sortable: false, width: 140 });
      return headers;
    },

    videoRows() {
      return this.byVideo.map((v) => {
        const method = (v.methods && v.methods[this.selectedMethod]) || {};
        const inWindow = method.overlapping_ideas;
        return {
          ...v,
          label: `${v.source_type === 'manual_analysis' ? 'Manual' : 'Interview'} #${v.source_id}`,
          incoherence_range:
            v.label_type === 'truthful'
              ? '-'
              : `${this.formatMsTenths(v.incoherence_start_ms)} - ${this.formatMsTenths(v.incoherence_end_ms)}`,
          method_flagged: method.subtracting_ideas || 0,
          // Only meaningful for lie videos with an analysis; null otherwise.
          method_in_window: inWindow === undefined || inWindow === null ? null : inWindow,
          method_outcome: method.outcome || null,
        };
      });
    },
  },

  watch: {
    advancedPanel(value) {
      localStorage.setItem(ADVANCED_PANEL_STORAGE_KEY, value === 0 ? '1' : '0');
    },
    appliedThreshold(value) {
      if (value === null) this.thresholdInput = '';
    },
  },

  methods: {
    bucketPresentation,

    /** Global metrics for one method key; always a full object (template-safe). */
    methodGlobal(key) {
      return (this.global.methods && this.global.methods[key]) || EMPTY_METHOD_GLOBAL;
    },

    applyThreshold() {
      const raw = String(this.thresholdInput ?? '').trim();
      if (raw === '') {
        this.thresholdError = '';
        this.$emit('apply-threshold', null);
        return;
      }
      const value = Number(raw);
      if (!Number.isFinite(value) || value < 0 || value > 100) {
        this.thresholdError = 'Enter a value between 0 and 100';
        return;
      }
      this.thresholdError = '';
      this.$emit('apply-threshold', value);
    },

    resetThreshold() {
      this.thresholdInput = '';
      this.thresholdError = '';
      this.$emit('apply-threshold', null);
    },

    formatPct(val) {
      if (val === null || val === undefined) return '—';
      return `${val}%`;
    },

    /** Milliseconds → "mm:ss.s" (tenths of a second). */
    formatMsTenths(ms) {
      const n = Number(ms);
      if (!Number.isFinite(n) || n < 0) return '—';
      const tenths = Math.round(n / 100);
      const m = Math.floor(tenths / 600);
      const s = (tenths - m * 600) / 10;
      return `${String(m).padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
    },

    /** "n of N <what>" line under a KPI value. */
    kpiSubtitle(methodKey, kpi) {
      const count = this.methodGlobal(methodKey)[kpi.countField] || 0;
      const total = this.global[kpi.denominatorField] || 0;
      return `${count} of ${total} ${kpi.subtitle}`;
    },

    kpiTarget(kpi) {
      return `target ${kpi.direction === 'max' ? '≤' : '≥'} ${kpi.target}%`;
    },

    /**
     * Target status for a KPI: 'ok' | 'ko' | 'none' (no data).
     * 'min' metrics must reach the target; 'max' metrics must stay at or below it.
     */
    kpiStatus(methodKey, kpi) {
      const pct = this.methodGlobal(methodKey)[kpi.pctField];
      if (pct === null || pct === undefined) return 'none';
      const meetsTarget = kpi.direction === 'max' ? pct <= kpi.target : pct >= kpi.target;
      return meetsTarget ? 'ok' : 'ko';
    },

    statusColor(status) {
      if (status === 'ok') return 'success';
      if (status === 'ko') return 'error';
      return 'grey';
    },

    kpiClass(status) {
      return status === 'none' ? '' : `ve-kpi--${status}`;
    },

    outcomeText(item) {
      const def = OUTCOME_DEFS[item.method_outcome];
      return def ? def.text : 'Unknown';
    },

    outcomeColor(item) {
      const def = OUTCOME_DEFS[item.method_outcome];
      return def ? def.color : 'grey';
    },

    shortRun(runId) {
      return String(runId).slice(0, 8);
    },

    sourceLabel(item) {
      return `${item.source_type === 'manual_analysis' ? 'Manual' : 'Interview'} #${item.source_id}`;
    },

    bucketItemSubtitle(item) {
      const parts = [this.sourceLabel(item)];
      if (item.bucket) parts.push(bucketPresentation(item.bucket).label);
      if (item.status) parts.push(String(item.status).toLowerCase());
      if (item.run_id) parts.push(`run ${this.shortRun(item.run_id)}`);
      return parts.join(' · ');
    },

    goToSource(item) {
      const withRun = this.showRunColumn || this.showBuckets;
      const route = withRun
        ? sourceRouteWithRun(item)
        : item.source_type === 'manual_analysis'
          ? `/coherence-analysis/${item.source_id}`
          : `/interview-analysis/${item.source_id}`;
      this.$emit('open-source', item);
      if (this.$router && route) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
/* Series identity line (group scope) */
.ve-series-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.ve-series-line__caption {
  margin-left: 4px;
}

/* Advanced summary row: corpus counts + threshold */
.ve-summary {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

@media (max-width: 900px) {
  .ve-summary {
    grid-template-columns: 1fr;
  }
}

.ve-counts-card {
  border-radius: 10px !important;
  padding: 16px 8px;
  display: flex;
  align-items: stretch;
}

.ve-count {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 8px;
  text-align: center;
}

.ve-count__value {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: #212121;
}

.ve-count__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
}

/* Corpus strip above the hero */
.ve-counts-card--corpus {
  position: relative;
  margin-bottom: 16px;
}

.ve-corpus-chip {
  position: absolute;
  top: 8px;
  right: 8px;
}

/* Hero: one card per coherence score method */
.ve-hero {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.ve-hero--advanced {
  margin-bottom: 0;
}

@media (max-width: 900px) {
  .ve-hero {
    grid-template-columns: 1fr;
  }
}

.ve-method-card {
  border-radius: 10px !important;
  padding: 16px 20px 20px;
}

.ve-method-card__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #333;
  margin-bottom: 12px;
}

/* The three KPIs inside a method card */
.ve-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

@media (max-width: 600px) {
  .ve-kpi-grid {
    grid-template-columns: 1fr;
  }
}

.ve-kpi {
  padding: 12px 14px;
  border-radius: 8px;
  border-left: 4px solid #bdbdbd;
  background: #fafafa;
}

.ve-kpi--ok {
  border-left-color: #4caf50;
}

.ve-kpi--ko {
  border-left-color: #f44336;
}

.ve-kpi__value {
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: #212121;
}

.ve-kpi__label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #333;
  margin-top: 8px;
}

.ve-kpi__hint {
  display: block;
  font-size: 10px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #757575;
}

.ve-kpi__subtitle {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
}

.ve-kpi__target {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #555;
  margin-top: 6px;
}

/* Buckets (group scope) */
.ve-bucket-alert {
  margin-bottom: 12px;
}

.ve-buckets {
  margin-bottom: 16px;
}

.ve-buckets .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px !important;
  margin-bottom: 6px;
}

.ve-bucket__header {
  font-size: 13px;
  font-weight: 700;
  color: #444;
}

.ve-bucket__list {
  background: transparent;
}

/* Advanced (collapsed by default) */
.ve-advanced {
  margin-bottom: 32px;
}

.ve-advanced .v-expansion-panel {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px !important;
}

.ve-advanced__header {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
}

.ve-method-section__title {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #757575;
  margin-bottom: 10px;
}

.ve-method-section__divider {
  margin: 16px 0;
}

.ve-method-card__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px 20px;
}

.ve-method-card__grid--pairs {
  grid-template-columns: 1fr 1fr;
}

.ve-method-stat__value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: #212121;
}

.ve-method-stat__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
  margin-top: 6px;
}

.ve-method-stat__subtitle {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
}

/* Threshold control */
.ve-threshold-card {
  border-radius: 10px !important;
  padding: 16px 20px;
}

.ve-threshold-card__label {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #555;
}

.ve-threshold-card__hint {
  font-size: 12px;
  color: #757575;
  margin: 4px 0 12px;
}

.ve-threshold-card__controls {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.ve-threshold-card__controls .v-btn {
  margin-top: 2px;
}

.ve-threshold-card__input {
  max-width: 120px;
}

/* Sections */
.ve-section {
  margin-bottom: 32px;
}

.ve-section__title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #555;
  padding: 0 4px;
  margin-bottom: 12px;
}

.ve-method-toggle {
  text-transform: none;
}

.ve-card {
  border-radius: 8px;
}

.ve-table {
  background: transparent !important;
}

.ve-mono {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  color: #424242;
}

.ve-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  text-align: center;
}
</style>
